import type { ReactNode } from 'react';
import { mascaraCPF, formatarNascimento } from '@/pages/inscricao/utils';
import { linkWhatsApp, formatarDataMinuto } from '../utils';
import type { InscritoGerenciar } from '../types';

interface Props {
  inscrito: InscritoGerenciar;
  eventoInicio: string;
  mesmaPessoa: boolean;
}

function calcularIdade(nascimento: string, referencia: string): number | null {
  const n = new Date(nascimento);
  const r = new Date(referencia);
  if (Number.isNaN(n.getTime()) || Number.isNaN(r.getTime())) return null;
  let idade = r.getFullYear() - n.getFullYear();
  const m = r.getMonth() - n.getMonth();
  if (m < 0 || (m === 0 && r.getDate() < n.getDate())) idade -= 1;
  return idade;
}

function Linha({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <p className="text-[13px] text-foreground-500">{rotulo}</p>
      <div className="mt-1 text-[15px] text-foreground-900">{children}</div>
    </div>
  );
}

export default function AbaDados({ inscrito, eventoInicio, mesmaPessoa }: Props) {
  const idade = inscrito.nascimento ? calcularIdade(inscrito.nascimento, eventoInicio) : null;

  return (
    <div className="space-y-3">
      <div className="rounded-2xl bg-background-50 p-4">
        <Linha rotulo="Nome">{inscrito.participante}</Linha>
        <Linha rotulo="CPF">{inscrito.cpf ? mascaraCPF(inscrito.cpf) : '—'}</Linha>
        <Linha rotulo="Data de nascimento">
          {inscrito.nascimento ? formatarNascimento(inscrito.nascimento) : '—'}
        </Linha>
        <Linha rotulo="Idade no dia do evento">
          {idade !== null ? `${idade} anos` : '—'}
        </Linha>
        <Linha rotulo="Telefone">
          {inscrito.participante_telefone ? (
            <a
              href={linkWhatsApp(inscrito.participante_telefone)}
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-primary-600 hover:underline"
            >
              {inscrito.participante_telefone}
            </a>
          ) : (
            '—'
          )}
        </Linha>
        <Linha rotulo="E-mail">
          {inscrito.participante_email ? (
            <a
              href={`mailto:${inscrito.participante_email}`}
              className="text-primary-600 hover:underline"
            >
              {inscrito.participante_email}
            </a>
          ) : (
            '—'
          )}
        </Linha>
      </div>

      <div className="rounded-2xl bg-background-50 p-4">
        <h4 className="text-[15px] font-semibold text-foreground-950">
          Responsável pela inscrição
        </h4>

        {mesmaPessoa ? (
          <p className="mt-3 text-[15px] text-foreground-500">
            A própria pessoa fez a inscrição.
          </p>
        ) : (
          <div className="mt-1">
            <Linha rotulo="Nome">{inscrito.responsavel}</Linha>
            <Linha rotulo="E-mail">
              {inscrito.responsavel_email ? (
                <a
                  href={`mailto:${inscrito.responsavel_email}`}
                  className="text-primary-600 hover:underline"
                >
                  {inscrito.responsavel_email}
                </a>
              ) : (
                '—'
              )}
            </Linha>
            <Linha rotulo="Telefone">
              {inscrito.responsavel_telefone ? (
                <a
                  href={linkWhatsApp(inscrito.responsavel_telefone)}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-primary-600 hover:underline"
                >
                  {inscrito.responsavel_telefone}
                </a>
              ) : (
                '—'
              )}
            </Linha>
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-background-50 p-4">
        <h4 className="text-[15px] font-semibold text-foreground-950">
          Histórico desta inscrição
        </h4>
        <div className="mt-1">
          <Linha rotulo="Inscreveu em">{formatarDataMinuto(inscrito.criado_em)}</Linha>
          {inscrito.validada_em && (
            <Linha rotulo="Validada em">{formatarDataMinuto(inscrito.validada_em)}</Linha>
          )}
          {(inscrito.subiu_em || inscrito.subiu_por || inscrito.subiu_motivo) && (
            <Linha rotulo="Virou suplente / subiu em">
              {[
                inscrito.subiu_em ? formatarDataMinuto(inscrito.subiu_em) : 'sem subida',
                inscrito.subiu_por === 'automatico'
                  ? 'automático'
                  : inscrito.subiu_por === 'direcao'
                    ? 'pela direção'
                    : null,
                inscrito.subiu_motivo,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Linha>
          )}
          {inscrito.cancelada_em && (
            <Linha rotulo="Cancelada em">
              {[
                formatarDataMinuto(inscrito.cancelada_em),
                inscrito.cancelamento_motivo,
                inscrito.cancelamento_detalhe,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Linha>
          )}
        </div>
      </div>
    </div>
  );
}